import { ModelName } from '../../models/names'
import Project from '../../providers/project'
import { ModelStorage } from './modelStorage';

interface WithId {
	_id?: string;
}

export class ModelHttpService {
	public async request<T>(url: string, method: string, data?: object): Promise<T> {
		const res = await fetch(url, {
			method, headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem("token") || "" },
			body: data ? JSON.stringify(data) : undefined,
		});
		if (res.status === 401) {
			ModelStorage.clearAndReload();
		}
		if (res.status !== 200) {
			let error = await res.json();
			throw new Error(error.message || res.statusText);
		}
		return await res.json();
	}	

	public async search<T>(searchTerm: string): Promise<T[]> {
		const url = `/api/users/search?searchTerm=${searchTerm}`;
		return await this.request<T[]>(url, "get");
	}

	public async searchCompanies<T>(searchTerm: string): Promise<T[]> {
		const url = `/api/users/company?searchTerm=${searchTerm}`;
		return await this.request<T[]>(url, "get");
	}

	public async findUserProject<T>(userId: string): Promise<T[]> {
		return await this.request<T[]>("/api/users/userProject", "post", { userId });
	}

	public async findProjectUsersProfile<T>(userIds: string[]): Promise<T[]> {
		return await this.request<T[]>("/api/users/getProjectUsersProfile", "post", { userIds });
	}

	public async findUserProfile<T>(email: string): Promise<T[]> {
		return await this.request<T[]>("/api/users/userProfile", "post", { email });
	}

	public async userFeedback<T>(userId: string, feedback: string, rating: number): Promise<T[]> {
		return await this.request<T[]>("/api/users/feedback", "post", { userId, feedback, rating });
	}

	public async getProjectInfoPDF<T>(projectId: string, userId: string): Promise<T> {
		return await this.request<T>("/api/users/getProjectInfoPDF", "post", { projectId, userId });
	}

	public async findUserUploads<T>(id: string, index: number): Promise<T[]> {
		return await this.request<T[]>("/api/users/userUploads", "post", { id, index });
	}

	public async find<T>(name: ModelName, constraints: Partial<T> = {}): Promise<T[]> {
		const data = encodeURI(JSON.stringify(constraints));
		const url = `/api/${name}/find?constraints=${data}`;
		return await this.request<T[]>(url, "get");
	}

	public async findCategoryWiseProduct<T>(name: ModelName, constraints: Record<string, unknown>): Promise<T[]> {
		const url = `/api/${name}/find-category-product`;
		return await this.request<T[]>(url, "post", constraints);
	}

	public async findSheetTemplate<T>(name: ModelName, constraints: Record<string, unknown>): Promise<T[]> {
		const url = `/api/${name}/get-sheet-template`;
		return await this.request<T[]>(url, "post", constraints);
	}

	public async getAllMaterials<T>(): Promise<T[]> {
		const url = `/api/getallmaterials`;
		return await this.request<T[]>(url, "get");
	}

	public async getMaterials<T>(): Promise<T[]> {
		const url = `/api/getmaterials`;
		return await this.request<T[]>(url, "get");
	}

	public async getAllGables<T>(): Promise<T[]> {
		const url = `/api/getallgables`;
		return await this.request<T[]>(url, "get");
	}

	public async allFuncRefresh<T>(): Promise<T[]> {
		const url = `/api/refresh`;
		return await this.request<T[]>(url, "get");
	}

	public async getcompanyusers<T>(name: ModelName, companyId = ""): Promise<T[]> {
		const url = `/api/${name}/getcompanyusers?companyId=${companyId}`;
		return await this.request<T[]>(url, "get");
	}

	public async getProjectcompanyusers<T>(name: ModelName, companyId = ""): Promise<T[]> {
		const url = `/api/${name}/companyusers?companyId=${companyId}`;
		return await this.request<T[]>(url, "get");
	}

	public async FindWaterDrainge<T>(name: ModelName, constraints: Partial<T> = {}, skip = 0, limit = 10): Promise<T[]> {
		const data = encodeURI(JSON.stringify(constraints));
		const url = `/api/${name}/find?constraints=${data}&skip=${skip}&limit=${limit}`;
		return await this.request<T[]>(url, "get");
	}

	public async findProducts<T>(name: ModelName, constraints: Partial<T> = {}, skip = 0, limit = 10): Promise<T[]> {
		const data = encodeURI(JSON.stringify(constraints));
		const url = `/api/${name}/find?constraints=${data}&skip=${skip}&limit=${limit}`;
		return await this.request<T[]>(url, "get");
	}

	public async getAllSheetProducts<T>(name: ModelName): Promise<T[]> {
		const url = `/api/${name}/get-all-sheet-template`;
		return await this.request<T[]>(url, "get");
	}

	public async getAllFunctions<T>(name: ModelName): Promise<T[]> {
		const url = `/api/${name}/get-all-functions`;
		return await this.request<T[]>(url, "get");
	}

	public async FindCompanies<T>(name: ModelName, constraints: Partial<T> = {}, skip = 0, limit = 10): Promise<T[]> {
		const data = encodeURI(JSON.stringify(constraints));
		const url = `/api/${name}/find?constraints=${data}&skip=${skip}&limit=${limit}`;
		return await this.request<T[]>(url, "get");
	}

	public async findById<T>(name: ModelName, id: Partial<T> = {}): Promise<T[]> {
		const url = `/api/${name}/findById?id=${id}`;
		return await this.request<T[]>(url, "get");
	}

	public async getUserProfile<T>(name: ModelName, userId: string): Promise<T[]> {
		const url = `/api/${name}/getuserprofile?userId=${userId}`;
		return await this.request<T[]>(url, "get");
	}

	public async getOrderItemsDetail<T>(name: ModelName, projectId: string): Promise<T[]> {
		const url = `/api/${name}/getorderitemsdetail`;
		return await this.request<T[]>(url, "post", { projectId });
	}

	public async create<T>(name: string, document: object): Promise<T> {
		const url = `/api/${name}/create`;
		return await this.request<T>(url, "post", { values: document });
	}

	public async deleteContactPerson<T>(name: string, document: object): Promise<T> {
		const url = `/api/${name}/deletecontactperson`;
		return await this.request<T>(url, "delete", { values: document });
	}

	public async registeruser<T>(name: string, document: object): Promise<T> {
		const url = `/api/${name}/registeruser`;
		return await this.request<T>(url, "post", document);
	}

	public async getOrdersdata<T>(document: object): Promise<T> {
		const url = `/api/users/getordersdata`;
		return await this.request<T>(url, "post", document);
	}
	public async update<T extends WithId>(name: ModelName, document: T): Promise<void> {
		const url = `/api/${name}/update`;
		let payload: object = document;
		if (name === ModelName.Project) {
			const projectData = new Project(document);
			payload = projectData.getProjectData();
		}
		return await this.request<void>(url, "put", { id: document._id, values: payload });
	}

	public async updateUserAccess<T extends WithId>(name: string, document: T): Promise<void> {
		const url = `/api/${name}/update`;
		return await this.request<void>(url, "put", { id: document._id, values: document });
	}

	public async updateUserProfile<T extends WithId>(name: ModelName, document: T): Promise<void> {
		const url = `/api/${name}/update`;
		return await this.request<void>(url, "put", { id: document._id, values: document });
	}

	public async geyWaterdrainageProd<T>(prodId: string): Promise<T[]> {
		const url = `/api/users/getwaterdrainageprod?prodId=${prodId}`;
		return await this.request<T[]>(url, "get");
	}

	public async inviteUserToProject<T>(document: object): Promise<T[]> {
		return await this.request<T[]>("/api/users/invites", "post", document);
	}

	public async updateUserCompany<T extends WithId>(name: ModelName, document: T): Promise<void> {
		const url = `/api/${name}/update`;
		return await this.request<void>(url, "put", { id: document._id, values: document });
	}

	public async updateProjectWithSpecificPayload<T extends WithId>(name: ModelName, document: T): Promise<void> {
		const url = `/api/${name}/update`;
		return await this.request<void>(url, "put", { id: document._id, values: document });
	}

	public async delete<T extends WithId>(name: ModelName, document: T): Promise<void> {
		const url = `/api/${name}/delete?id=${document._id}`;
		return await this.request<void>(url, "delete");
	}

	public async deleteCompanyUser(userId: string): Promise<void> {
		const url = `/api/users/removeuserfromcompany`;
		return await this.request<void>(url, "post", { userId });
	}

	public async deleteUserAccess<T extends WithId>(document: T): Promise<void> {
		const url = `/api/useraccesses/delete?id=${document._id}`;
		return await this.request<void>(url, "delete");
	}

	public async removeCompanyData<T>(companyId: string): Promise<T[]> {
		return await this.request<T[]>("/api/users/removecompany", "post", { companyId });
	}

	// =======================================================================
	// NEW: FormData-based upload methods (Fas 2)
	// =======================================================================

	/**
	 * Upload a file via multipart/form-data to the new generic endpoint.
	 * Does NOT set Content-Type header — browser adds multipart boundary automatically.
	 */
	public async uploadFormData<T>(url: string, formData: FormData): Promise<T> {
		const res = await fetch(url, {
			method: 'POST',
			headers: { 'Authorization': localStorage.getItem('token') || '' },
			body: formData,
		});
		if (res.status === 401) {
			ModelStorage.clearAndReload();
		}
		if (res.status !== 200) {
			const error = await res.json();
			throw new Error(error.message || res.statusText);
		}
		return await res.json();
	}

	/**
	 * Remove a file from the uploads directory.
	 */
	public async removeUpload(filePath: string): Promise<{ deleted: boolean }> {
		return await this.request<{ deleted: boolean }>("/api/users/remove-upload", "post", { path: filePath });
	}
}
